/* ==========================================================================
   Dreamey PDP — Variant selection, gallery tracking, sticky ATC bar
   ========================================================================== */

(function () {
  'use strict';

  /* ── Shared easing ── */
  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  /* ——————————————————————————————————————————
     VARIANT PICKER
  —————————————————————————————————————————— */
  function initVariantPicker() {
    const form = document.querySelector('.dreamey-pdp__form');
    if (!form) return;

    const variantInput = document.getElementById('dreamey-pdp-variant-id');
    const optionButtons = form.querySelectorAll('.dreamey-pdp__option-btn, .dreamey-pdp__color-btn');

    if (!optionButtons.length) return;

    // Track currently selected options
    const selectedOptions = {};
    form.querySelectorAll('[data-option-index]').forEach(function (group) {
      const index = group.dataset.optionIndex;
      const selected = group.querySelector('.is-selected');
      if (selected) selectedOptions[index] = selected.dataset.value;
    });

    // Strike through option values that are out of stock anywhere in the catalog
    // (kept clickable so shoppers can still preview their images).
    refreshAvailability(form);

    optionButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const optionIndex = this.dataset.optionIndex;
        const value = this.dataset.value;

        // Deselect siblings (both regular option buttons and color swatches)
        const isColor = this.classList.contains('dreamey-pdp__color-btn');
        const siblingClass = isColor ? '.dreamey-pdp__color-btn' : '.dreamey-pdp__option-btn';
        const siblings = form.querySelectorAll(
          '[data-option-index="' + optionIndex + '"]' + siblingClass
        );
        siblings.forEach(function (s) {
          s.classList.remove('is-selected');
          s.setAttribute('aria-pressed', 'false');
        });

        // Select this button
        this.classList.add('is-selected');
        this.setAttribute('aria-pressed', 'true');
        selectedOptions[optionIndex] = value;

        // Update label
        const labelEl = document.getElementById(
          'dreamey-pdp-option-' + (parseInt(optionIndex) + 1) + '-selected'
        );
        if (labelEl) labelEl.textContent = value;

        // Find matching variant
        updateVariant(selectedOptions, variantInput, form);
      });
    });
  }

  function updateVariant(selectedOptions, variantInput, form) {
    if (!window.__dreameyVariants) {
      console.warn('[Dreamey PDP] __dreameyVariants missing — variant switch inert. Hard-refresh to repopulate.');
      return;
    }

    const variants = window.__dreameyVariants;
    const optionValues = Object.keys(selectedOptions)
      .sort()
      .map(function (k) { return selectedOptions[k]; });

    const match = variants.find(function (v) {
      const vOptions = [v.option1, v.option2, v.option3].filter(Boolean);
      return optionValues.every(function (val, i) { return vOptions[i] === val; });
    });

    if (!match) return;

    // Update hidden input
    if (variantInput) variantInput.value = match.id;

    // Update price display
    const priceBlock = document.getElementById('dreamey-pdp-price');
    if (priceBlock && match.price) {
      const price = formatMoney(match.price);
      const compareAtPrice = match.compare_at_price
        ? formatMoney(match.compare_at_price)
        : null;

      // Was/now order: crossed-out anchor first, then the real price
      let html = '';
      const onSale = compareAtPrice && match.compare_at_price > match.price;
      if (onSale) {
        html += '<span class="dreamey-pdp__price-compare">' + compareAtPrice + '</span>';
      }
      html += '<span class="dreamey-pdp__price-sale">' + price + '</span>';
      if (onSale) {
        const pct = Math.round(
          ((match.compare_at_price - match.price) / match.compare_at_price) * 100
        );
        html += '<span class="dreamey-pdp__price-badge">SAVE ' + pct + '%</span>';
      }
      priceBlock.innerHTML = html;
    }

    // Update ATC bar price
    const barPrice = document.getElementById('dreamey-atc-bar-price');
    if (barPrice && match.price) barPrice.textContent = formatMoney(match.price);

    // Update ATC button state — toggle classes + inner <span> only so we don't
    // wipe out the loading spinner that product-form.js depends on.
    function setBtnState(btn, available) {
      if (!btn) return;
      btn.disabled = !available;
      btn.classList.toggle('dreamey-btn--accent', available);
      btn.classList.toggle('dreamey-pdp__atc-btn--unavailable', !available);
      const span = btn.querySelector('span');
      if (span) {
        span.textContent = available ? (btn.dataset.defaultText || 'Add to Cart') : 'Sold Out';
      }
    }

    setBtnState(form.querySelector('.dreamey-pdp__atc-btn'), match.available);
    setBtnState(document.getElementById('dreamey-atc-bar-btn'), match.available);

    // Update gallery images for selected variant
    updateGallery(match.id);
  }

  // Toggle `.is-unavailable` on each color/size button. A value is marked out of
  // stock only when NO variant carrying it is available (global availability) —
  // so genuinely buyable colors/sizes are never struck, even while the shopper
  // is sitting on a sold-out combination. This state is fixed for the product,
  // so it's computed once on load. Buttons stay selectable for previews.
  function refreshAvailability(form) {
    const variants = window.__dreameyVariants;
    if (!variants || !variants.length) return;

    form.querySelectorAll('.dreamey-pdp__option-btn, .dreamey-pdp__color-btn').forEach(function (btn) {
      const dim = parseInt(btn.dataset.optionIndex, 10);
      const value = btn.dataset.value;

      const inStock = variants.some(function (v) {
        return v.available && [v.option1, v.option2, v.option3][dim] === value;
      });

      btn.classList.toggle('is-unavailable', !inStock);
    });
  }

  function updateGallery(variantId) {
    if (!window.__dreameyImages || !window.__dreameyImages.length) return;
    if (!window.__dreameyVariantImages) return;

    // Find the featured image ID for this variant
    var featuredImageId = window.__dreameyVariantImages[String(variantId)];
    if (!featuredImageId) return;

    // Get unique featured image IDs (one per color) and their positions
    var uniqueFeaturedIds = [];
    var seen = {};
    Object.keys(window.__dreameyVariantImages).forEach(function (key) {
      var imgId = window.__dreameyVariantImages[key];
      if (!seen[imgId]) {
        seen[imgId] = true;
        uniqueFeaturedIds.push(imgId);
      }
    });

    // Find position indexes of each featured image in the images array
    var featuredPositions = uniqueFeaturedIds.map(function (fid) {
      return window.__dreameyImages.findIndex(function (img) { return img.id === fid; });
    }).sort(function (a, b) { return a - b; });

    // Find which group the current variant's featured image belongs to
    var currentFeaturedIdx = window.__dreameyImages.findIndex(function (img) {
      return img.id === featuredImageId;
    });
    if (currentFeaturedIdx === -1) return;

    // Determine the range: from this featured image to the next featured image
    var groupStart = currentFeaturedIdx;
    var groupEnd = window.__dreameyImages.length; // default to end
    for (var i = 0; i < featuredPositions.length; i++) {
      if (featuredPositions[i] > currentFeaturedIdx) {
        groupEnd = featuredPositions[i];
        break;
      }
    }

    // Only show images for the selected color group
    // (generic/hero images uploaded before the first featured image are
    // visible on initial page load but hidden once a color is selected)
    var imagesToShow = window.__dreameyImages.slice(groupStart, groupEnd);

    // No images for this color group — leave the current gallery alone rather
    // than wipe it to a blank stack with a stale '01' counter.
    if (!imagesToShow.length) return;

    function buildGalleryItem(img, i, mobile) {
      var item = document.createElement('div');
      item.className = mobile ? 'dreamey-pdp__swiper-slide' : 'dreamey-pdp__gallery-item';
      if (!mobile) item.dataset.index = String(i + 1);
      var image = document.createElement('img');
      image.className = 'dreamey-pdp__gallery-img';
      image.src = mobile ? img.srcMobile : img.src;
      // setAttribute/property assignment escapes the string — prevents the
      // `alt='5" pillowcase'` → broken-attribute bug from raw template concat.
      image.alt = img.alt || '';
      if (i > 0) image.loading = 'lazy';
      if (!mobile) image.sizes = '(max-width: 767px) 100vw, 50vw';
      item.appendChild(image);
      return item;
    }

    // Update desktop gallery stack
    var stack = document.querySelector('.dreamey-pdp__gallery-stack');
    if (stack) {
      stack.textContent = '';
      imagesToShow.forEach(function (img, i) { stack.appendChild(buildGalleryItem(img, i, false)); });
    }

    // Update mobile swiper
    var swiper = document.getElementById('dreamey-pdp-swiper');
    if (swiper) {
      swiper.textContent = '';
      imagesToShow.forEach(function (img, i) { swiper.appendChild(buildGalleryItem(img, i, true)); });
    }

    // Update dots
    var dots = document.getElementById('dreamey-pdp-dots');
    if (dots) {
      dots.innerHTML = imagesToShow.map(function (img, i) {
        return '<button class="dreamey-pdp__gallery-dot' + (i === 0 ? ' is-active' : '') +
          '" aria-label="Image ' + (i + 1) + '"></button>';
      }).join('');
    }

    // Update counter
    var counter = document.getElementById('dreamey-pdp-counter-current');
    if (counter) counter.textContent = '01';

    // Scroll gallery back to top
    if (stack) stack.scrollTop = 0;
    if (swiper) swiper.scrollLeft = 0;

    // Re-init gallery observer
    initGalleryTracker();
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    // Prefer Shopify.formatMoney when available — honors the store's money format.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.__dreameyMoneyFormat);
    }
    // Fallback: honor window.__dreameyMoneyFormat placeholders from Liquid.
    var format = window.__dreameyMoneyFormat || "${{amount}}";
    function withCommas(num) {
      var parts = num.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    var value;
    if (format.indexOf('amount_no_decimals_with_comma_separator') > -1) {
      value = withCommas(Math.round(cents / 100).toString()).replace(/,/g, '.');
    } else if (format.indexOf('amount_no_decimals') > -1) {
      value = withCommas(Math.round(cents / 100).toString());
    } else if (format.indexOf('amount_with_comma_separator') > -1) {
      value = withCommas((cents / 100).toFixed(2)).replace('.', ',').replace(/,(\d{2})$/, ',$1');
    } else {
      value = withCommas((cents / 100).toFixed(2));
    }
    return format.replace(/\{\{\s*(amount[^}]*)\s*\}\}/g, value);
  }

  /* ——————————————————————————————————————————
     QUANTITY CONTROLS
  —————————————————————————————————————————— */
  function initQtyControls() {
    const qtyInput = document.getElementById('dreamey-pdp-qty');
    if (!qtyInput) return;

    function getBounds() {
      return {
        min: parseInt(qtyInput.min) || 1,
        max: parseInt(qtyInput.max) || 99,
      };
    }

    function syncButtons() {
      const bounds = getBounds();
      const current = parseInt(qtyInput.value) || bounds.min;
      document.querySelectorAll('.dreamey-pdp__qty-btn').forEach(function (btn) {
        const action = btn.dataset.action;
        if (action === 'minus') btn.disabled = current <= bounds.min;
        if (action === 'plus') btn.disabled = current >= bounds.max;
      });
    }

    function clamp() {
      const bounds = getBounds();
      // Strip any non-digit characters (paste of "3 pcs", etc.) and clamp to bounds.
      var raw = String(qtyInput.value).replace(/[^\d]/g, '');
      var parsed = parseInt(raw);
      if (isNaN(parsed) || parsed < bounds.min) parsed = bounds.min;
      if (parsed > bounds.max) parsed = bounds.max;
      qtyInput.value = String(parsed);
      syncButtons();
    }

    qtyInput.addEventListener('input', function () {
      // Normalize non-digits on every keystroke without fighting the caret —
      // only strip, don't clamp while typing.
      var cleaned = String(qtyInput.value).replace(/[^\d]/g, '');
      if (cleaned !== qtyInput.value) qtyInput.value = cleaned;
    });
    qtyInput.addEventListener('change', clamp);
    qtyInput.addEventListener('blur', clamp);

    document.querySelectorAll('.dreamey-pdp__qty-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const bounds = getBounds();
        const current = parseInt(qtyInput.value) || bounds.min;
        const action = this.dataset.action;

        if (action === 'minus' && current > bounds.min) {
          qtyInput.value = current - 1;
        } else if (action === 'plus' && current < bounds.max) {
          qtyInput.value = current + 1;
        }
        syncButtons();
      });
    });

    syncButtons();
  }

  /* ——————————————————————————————————————————
     GALLERY SCROLL TRACKER (desktop)
  —————————————————————————————————————————— */
  var galleryObserver = null;

  function initGalleryTracker() {
    const galleryItems = document.querySelectorAll('.dreamey-pdp__gallery-item[data-index]');
    const counter = document.getElementById('dreamey-pdp-counter-current');
    if (!galleryItems.length || !counter) return;

    // Disconnect previous observer to prevent leaks on variant switch
    if (galleryObserver) galleryObserver.disconnect();

    galleryObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            const index = entry.target.dataset.index;
            counter.textContent = String(index).padStart(2, '0');
          }
        });
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    );

    galleryItems.forEach(function (item) { galleryObserver.observe(item); });
  }

  /* ——————————————————————————————————————————
     STICKY ATC BAR
  —————————————————————————————————————————— */
  function initStickyATC() {
    const atcBar = document.getElementById('dreamey-pdp-atc-bar');
    const anchor = document.getElementById('dreamey-pdp-atc-anchor');
    if (!atcBar || !anchor) return;

    // On phones, also hide while the bundle cards are on screen so buy buttons
    // never stack. Desktop keeps its existing behaviour untouched.
    const bundles = document.querySelector('.dreamey-pdp-bundles__grid');
    const phone = window.matchMedia ? window.matchMedia('(max-width: 767px)') : { matches: false };
    let atcAbove = false, bundlesIn = false, barVisible = null;

    function apply() {
      const shouldShow = atcAbove && !(bundlesIn && phone.matches);
      if (shouldShow === barVisible) return;
      barVisible = shouldShow;
      atcBar.classList.toggle('is-visible', shouldShow);
      atcBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }

    // Show only once the ATC has scrolled ABOVE the viewport. "Not intersecting"
    // alone also fires while the ATC is still below the fold, which on a phone is
    // the state at page load, so the bar used to cover the gallery instantly.
    new IntersectionObserver(function (entries) {
      const e = entries[entries.length - 1];
      atcAbove = !e.isIntersecting && e.boundingClientRect.top < 0;
      apply();
    }, { threshold: 0 }).observe(anchor);

    if (bundles) {
      new IntersectionObserver(function (entries) {
        bundlesIn = entries[entries.length - 1].isIntersecting;
        apply();
      }, { threshold: 0 }).observe(bundles);
    }
  }

  /* ——————————————————————————————————————————
     STICKY INFO PANEL — pin when bottom of info aligns with viewport bottom
     Desktop-only (>=992px). Idempotent: tears down prior listeners on re-init.
  —————————————————————————————————————————— */
  var stickyInfoState = { scroll: null, resize: null };

  function initStickyInfo() {
    var info = document.querySelector('.dreamey-pdp__info');
    var pdp = document.querySelector('.dreamey-pdp');
    if (!info || !pdp) return;

    // Teardown any prior listeners before re-binding (Shopify theme editor
    // re-renders a section without tearing down its scripts).
    if (stickyInfoState.scroll) window.removeEventListener('scroll', stickyInfoState.scroll);
    if (stickyInfoState.resize) window.removeEventListener('resize', stickyInfoState.resize);
    stickyInfoState.scroll = null;
    stickyInfoState.resize = null;

    // Desktop only — bail on mobile without attaching anything.
    if (!window.matchMedia('(min-width: 992px)').matches) {
      info.style.top = '';
      return;
    }

    var navH, infoH, viewH, overflow, ticking;

    function measure() {
      navH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-height')) || 60;
      infoH = info.offsetHeight;
      viewH = window.innerHeight;
      overflow = infoH - (viewH - navH);
    }

    function onScroll() {
      if (overflow <= 0) {
        info.style.top = navH + 'px';
        return;
      }
      var pdpRect = pdp.getBoundingClientRect();
      var scrolled = navH - pdpRect.top;
      var maxScroll = pdp.offsetHeight - viewH + navH;
      var progress = Math.max(0, Math.min(1, scrolled / maxScroll));
      var top = navH - (overflow * progress);
      info.style.top = top + 'px';
    }

    function onScrollThrottled() {
      if (!ticking) {
        requestAnimationFrame(function () {
          onScroll();
          ticking = false;
        });
        ticking = true;
      }
    }

    function onResize() {
      // Cross media-query boundary: rebind at the new size.
      if (!window.matchMedia('(min-width: 992px)').matches) {
        window.removeEventListener('scroll', onScrollThrottled);
        window.removeEventListener('resize', onResize);
        stickyInfoState.scroll = null;
        stickyInfoState.resize = null;
        info.style.top = '';
        return;
      }
      measure();
      onScroll();
    }

    measure();
    onScroll();
    window.addEventListener('scroll', onScrollThrottled, { passive: true });
    window.addEventListener('resize', onResize);
    stickyInfoState.scroll = onScrollThrottled;
    stickyInfoState.resize = onResize;
  }

  /* ——————————————————————————————————————————
     STAT COUNT-UP ANIMATION
  —————————————————————————————————————————— */
  function initCountUp() {
    var els = document.querySelectorAll('[data-count-to]');
    if (!els.length) return;

    // Skip animation for users who prefer reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach(function (el) {
        var target = parseFloat(el.dataset.countTo);
        var decimals = parseInt(el.dataset.countDecimals || '0', 10);
        var suffix = el.dataset.countSuffix || '';
        el.textContent = target.toFixed(decimals) + suffix;
      });
      return;
    }

    // Observe the parent container so all stats trigger together with stagger
    var container = els[0].closest('.dreamey-pdp-sust__stats');
    if (!container) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);

        els.forEach(function (el, i) {
          var from = parseFloat(el.dataset.countFrom || '0');
          var target = parseFloat(el.dataset.countTo);
          var decimals = parseInt(el.dataset.countDecimals || '0', 10);
          var suffix = el.dataset.countSuffix || '';
          var duration = 1200;
          var delay = i * 150;

          el.textContent = from.toFixed(decimals) + suffix;

          setTimeout(function () {
            var start = performance.now();

            function tick(now) {
              var elapsed = now - start;
              var progress = Math.min(elapsed / duration, 1);
              var eased = easeOutExpo(progress);
              var current = from + (target - from) * eased;
              el.textContent = current.toFixed(decimals) + suffix;
              if (progress < 1) requestAnimationFrame(tick);
            }

            requestAnimationFrame(tick);
          }, delay);
        });
      });
    }, { threshold: 0.3 });

    // Set initial display values immediately
    els.forEach(function (el) {
      var from = parseFloat(el.dataset.countFrom || '0');
      var decimals = parseInt(el.dataset.countDecimals || '0', 10);
      var suffix = el.dataset.countSuffix || '';
      el.textContent = from.toFixed(decimals) + suffix;
    });

    observer.observe(container);
  }

  /* ——————————————————————————————————————————
     MOBILE GALLERY PAGER DOTS
     Updates .is-active on the mobile swiper dots as the user scrolls,
     and scrolls the swiper when a dot is clicked.
  —————————————————————————————————————————— */
  function initSwiperPager() {
    var swiper = document.getElementById('dreamey-pdp-swiper');
    var dotsWrap = document.getElementById('dreamey-pdp-dots');
    if (!swiper || !dotsWrap) return;

    function setActive(index) {
      var dots = dotsWrap.querySelectorAll('.dreamey-pdp__gallery-dot');
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
      });
    }

    var ticking = false;
    swiper.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var slideWidth = swiper.clientWidth;
        if (slideWidth > 0) {
          setActive(Math.round(swiper.scrollLeft / slideWidth));
        }
        ticking = false;
      });
    }, { passive: true });

    dotsWrap.addEventListener('click', function (e) {
      var btn = e.target.closest('.dreamey-pdp__gallery-dot');
      if (!btn) return;
      var dots = Array.prototype.slice.call(dotsWrap.querySelectorAll('.dreamey-pdp__gallery-dot'));
      var index = dots.indexOf(btn);
      if (index < 0) return;
      swiper.scrollTo({ left: index * swiper.clientWidth, behavior: 'smooth' });
    });
  }

  /* ——————————————————————————————————————————
     BENTO SCROLL REVEAL (mobile)
     The "Results" bento stack: the card nearest the viewport
     centre stays full; cards above/below mute (opacity only —
     no movement, so the hairline stack never shifts). Respects
     reduced-motion and only runs at the mobile (stacked) width.
  —————————————————————————————————————————— */
  function initBentoReveal() {
    var tiles = Array.prototype.slice.call(document.querySelectorAll('.dreamey-bento__tile'));
    if (!tiles.length) return;

    var mqMobile = window.matchMedia('(max-width: 749px)');
    var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var ticking = false;

    function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }
    function smooth(t) { return t * t * (3 - 2 * t); }

    function frame() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      for (var i = 0; i < tiles.length; i++) {
        var r = tiles[i].getBoundingClientRect();
        var p = (r.top + r.height / 2) / vh;            // 0 = viewport top, 1 = bottom
        var d = Math.abs(p - 0.5);                       // distance from centre
        var t = smooth(clamp((d - 0.12) / (0.46 - 0.12), 0, 1));
        tiles[i].style.opacity = (1 - 0.7 * t).toFixed(3); // full at centre → 0.30 at edges
      }
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }
    function clearStyles() { tiles.forEach(function (el) { el.style.opacity = ''; }); }

    function apply() {
      if (mqMobile.matches && !mqReduce.matches) {
        document.documentElement.classList.add('bento-anim-ready');
        frame();
        window.addEventListener('scroll', onScroll, { passive: true });
      } else {
        document.documentElement.classList.remove('bento-anim-ready');
        window.removeEventListener('scroll', onScroll);
        clearStyles();
      }
    }

    window.addEventListener('resize', apply);
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', apply);
    if (mqReduce.addEventListener) mqReduce.addEventListener('change', apply);
    apply();
  }

  /* ——————————————————————————————————————————
     REVIEWS MARQUEE
     Ported from the skin-listicle landing page so both read as one component.
     The section renders the review set twice; the loop resets at the halfway
     mark, which is invisible because both halves are identical.
  —————————————————————————————————————————— */
  function initReviewsMarquee() {
    const wrap = document.getElementById('dreamey-pdp-reviews-marquee');
    if (!wrap) return;

    const track = wrap.querySelector('.dreamey-pdp-reviews__track');
    if (!track || !track.children.length) return;

    // Resetting at the halfway mark only works if the track can actually scroll
    // that far, so the two rendered passes have to span at least two viewports.
    // PDPs carry fewer reviews than the landing page, so top the track up here.
    // Doubling whole copies keeps both halves identical.
    let guard = 0;
    while (track.scrollWidth < wrap.clientWidth * 2 && guard < 6) {
      const copies = Array.prototype.slice.call(track.children);
      copies.forEach(function (card) {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button, [tabindex]').forEach(function (el) {
          el.setAttribute('tabindex', '-1');
        });
        track.appendChild(clone);
      });
      guard++;
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const SPEED = 0.35; // px per frame

    let half = 0;
    let pos = 0;
    let dragging = false;
    let hover = false;
    let startX = 0;
    let startScroll = 0;

    function measure() {
      half = track.scrollWidth / 2;
    }
    measure();

    function step() {
      if (!half) measure();
      if (!reduced && !dragging && !hover && half) {
        // Accumulate a float position: mobile browsers round scrollLeft to
        // integers, so `+= 0.35` alone never advances.
        pos += SPEED;
        if (pos >= half) pos -= half;
        wrap.scrollLeft = pos;
      } else {
        pos = wrap.scrollLeft;
      }
      requestAnimationFrame(step);
    }
    requestAnimationFrame(step);

    window.addEventListener('load', measure);
    window.addEventListener('resize', measure);

    wrap.addEventListener('mouseenter', function () { hover = true; });
    wrap.addEventListener('mouseleave', function () { hover = false; });
    wrap.addEventListener('dragstart', function (e) { e.preventDefault(); });

    wrap.addEventListener('pointerdown', function (e) {
      dragging = true;
      wrap.classList.add('is-dragging');
      startX = e.clientX;
      startScroll = wrap.scrollLeft;
      try { wrap.setPointerCapture(e.pointerId); } catch (err) { /* no capture, drag still works */ }
    });

    wrap.addEventListener('pointermove', function (e) {
      if (!dragging || !half) return;
      wrap.scrollLeft = startScroll - (e.clientX - startX);
      // Keep a dragged track inside the first half so it can loop either way
      if (wrap.scrollLeft <= 0) {
        wrap.scrollLeft += half;
        startScroll += half;
      } else if (wrap.scrollLeft >= half) {
        wrap.scrollLeft -= half;
        startScroll -= half;
      }
    });

    function endDrag() {
      dragging = false;
      wrap.classList.remove('is-dragging');
    }
    wrap.addEventListener('pointerup', endDrag);
    wrap.addEventListener('pointercancel', endDrag);
  }

  /* ——————————————————————————————————————————
     INIT
     Note: section scroll-reveal is handled by dreamey-animations.js (global)
  —————————————————————————————————————————— */
  function init() {
    initVariantPicker();
    initQtyControls();
    initGalleryTracker();
    initSwiperPager();
    initStickyATC();
    initStickyInfo();
    initCountUp();
    initBentoReveal();
    initReviewsMarquee();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
